import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import { behindStories, guideResources, viewingTips } from "@/mocks/guide";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Guide() {
  usePageMeta(
    "观影指南：完整版观看建议与幕后资料 - 色戒未删减版",
    "色戒未删减版观影指南，包含完整版观看建议、注意力分配方式、拍摄花絮与幕后故事，以及相关影视资料与延伸阅读推荐。",
    "色戒观影指南,完整版观看建议,拍摄花絮,幕后故事,影视资料,观影技巧",
  );

  return (
    <SiteLayout>
      <header className="relative overflow-hidden border-b border-background-800/70 pt-[72px]">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20interior%20of%20a%20vintage%20movie%20theater%20lobby%20with%20deep%20crimson%20velvet%20drapery%2C%20brass%20sconces%2C%20framed%20vintage%20film%20posters%2C%20warm%20amber%20light%20and%20soft%20haze%2C%20polished%20dark%20wood%20floor%20reflections%2C%20elegant%20art%20deco%20details%2C%20moody%20nostalgic%20atmosphere%2C%20no%20text&width=1920&height=700&seq=sejie-guide-header-01&orientation=landscape"
          alt="观影指南页视觉背景"
          title="色戒完整版 - 观影指南"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/88 to-background-950/45" />
        <div className="relative z-10 mx-auto max-w-[1180px] px-4 pb-14 pt-12 md:px-8 md:pb-16 md:pt-16">
          <nav className="flex flex-wrap items-center gap-2 text-[12px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <span className="text-foreground-300">观影指南</span>
          </nav>
          <p className="mt-6 font-label text-[10px] uppercase tracking-[0.28em] text-accent-400">
            Viewing Guide
          </p>
          <h1 className="mt-4 font-heading text-[30px] leading-[1.25] text-foreground-50 md:text-[44px]">
            观影指南：把完整版看完的正确方式
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-[1.95] text-foreground-300">
            完整版的价值藏在节奏里。这一页整理观看建议、幕后故事与相关资料入口，帮你安排好观影时间，也帮你在看第二遍时找到新的观察角度。
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {[
              { label: "观影建议", href: "#tips" },
              { label: "幕后花絮", href: "#behind" },
              { label: "相关资源", href: "#resources" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="whitespace-nowrap rounded-full border border-foreground-50/20 bg-background-950/40 px-4 py-2 text-[12px] text-foreground-200 transition-colors hover:border-accent-500/60 hover:text-accent-300"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </header>

      <section id="tips" className="scroll-mt-[96px] bg-background-950 py-14 md:py-20">
        <div className="mx-auto max-w-[1180px] px-4 md:px-8">
          <SectionHeading
            eyebrow="Before Watching"
            title="观影建议：先理解观看方式"
            desc="同一部电影，不同的观看环境会带来完全不同的感受。以下四条建议来自我们反复重看的经验。"
          />
          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-2">
            {viewingTips.map((tip, index) => (
              <div
                key={tip.title}
                className="group flex gap-4 rounded-lg border border-background-800/80 bg-background-900/40 p-5 transition-colors hover:border-primary-700/60 md:p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-accent-500/40 bg-background-950/60 text-accent-300">
                  <i className={`${tip.icon} text-lg`} />
                </span>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-label text-[11px] text-foreground-600">
                      0{index + 1}
                    </span>
                    <h2 className="font-heading text-base text-foreground-50 md:text-[17px]">
                      {tip.title}
                    </h2>
                  </div>
                  <p className="mt-2.5 text-[13px] leading-[1.9] text-foreground-400">
                    {tip.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-4 rounded-lg border border-primary-900/60 bg-primary-950/50 p-5 sm:flex-row sm:items-center sm:justify-between md:p-6">
            <div className="flex items-start gap-3">
              <i className="ri-information-line mt-0.5 text-lg text-accent-400" />
              <p className="text-[13px] leading-relaxed text-primary-100/80">
                本站不提供影片播放与下载，仅整理资料与分析。请通过正规渠道观看，尊重版权。
              </p>
            </div>
            <Link
              to="/category?type=version"
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-500/50 px-5 text-sm text-accent-300 transition-colors hover:bg-accent-500/10"
            >
              查看版本考据
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <section
        id="behind"
        className="scroll-mt-[96px] border-t border-background-800/70 bg-background-900/30 py-14 md:py-20"
      >
        <div className="mx-auto max-w-[1180px] px-4 md:px-8">
          <SectionHeading
            eyebrow="Behind The Scenes"
            title="拍摄花絮与幕后故事"
            desc="从老街的搭建到旗袍的剪裁，幕后团队如何为一段虚构的故事造出可信的世界。"
          />
          <div className="mt-9 space-y-8 md:space-y-10">
            {behindStories.map((story, index) => (
              <div
                key={story.title}
                className={`grid grid-cols-1 items-center gap-6 rounded-lg border border-background-800/80 bg-background-900/40 p-4 md:grid-cols-2 md:gap-8 md:p-5 ${
                  index % 2 === 1 ? "md:[&>figure]:order-2" : ""
                }`}
              >
                <figure className="overflow-hidden rounded-md">
                  <img
                    src={story.image}
                    alt={`${story.title}幕后示意图`}
                    title={`${story.title} - 色戒拍摄花絮`}
                    className="aspect-[3/2] w-full object-cover object-top"
                  />
                </figure>
                <div className="px-1 pb-2 md:px-3">
                  <span className="font-label text-[10px] uppercase tracking-[0.22em] text-accent-400">
                    Story 0{index + 1}
                  </span>
                  <h3 className="mt-2 font-heading text-xl text-foreground-50 md:text-2xl">
                    {story.title}
                  </h3>
                  <p className="mt-3 text-[13.5px] leading-[1.95] text-foreground-400">
                    {story.body}
                  </p>
                  <Link
                    to="/category?type=behind"
                    className="mt-5 inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-accent-300 transition-colors hover:text-accent-200"
                  >
                    查看更多幕后内容
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="resources"
        className="scroll-mt-[96px] border-t border-background-800/70 bg-background-950 py-14 md:py-20"
      >
        <div className="mx-auto max-w-[1180px] px-4 md:px-8">
          <SectionHeading
            eyebrow="Resources"
            title="相关资源与延伸阅读"
            desc="按主题整理的资料入口，适合在观影前后按需查阅。"
            action={{ label: "全部资料", to: "/category" }}
          />
          <div className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-2">
            {guideResources.map((resource) => (
              <Link
                key={resource.title}
                to={resource.to}
                className="group flex items-start justify-between gap-4 rounded-lg border border-background-800/80 bg-background-900/40 p-5 transition-colors hover:border-primary-700/60 hover:bg-background-900"
              >
                <div>
                  <span className="rounded-full bg-secondary-900/70 px-2.5 py-1 text-[10px] text-secondary-200">
                    {resource.type}
                  </span>
                  <h3 className="mt-3 font-heading text-[15px] leading-snug text-foreground-50 transition-colors group-hover:text-accent-300">
                    {resource.title}
                  </h3>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-foreground-500">
                    {resource.desc}
                  </p>
                </div>
                <i className="ri-arrow-right-up-line mt-1 shrink-0 text-foreground-600 transition-colors group-hover:text-accent-400" />
              </Link>
            ))}
            <div className="flex flex-col justify-between rounded-lg border border-primary-900/60 bg-primary-950/50 p-5">
              <div>
                <span className="font-label text-[10px] uppercase tracking-[0.22em] text-accent-400">
                  Special
                </span>
                <h3 className="mt-3 font-heading text-[15px] leading-snug text-foreground-50">
                  剧情解析专题：逐章拆解《色戒》
                </h3>
                <p className="mt-2 text-[12.5px] leading-relaxed text-primary-100/70">
                  按时间顺序梳理六个章节，配合目录联动，适合在二刷前通读一遍。
                </p>
              </div>
              <Link
                to="/special/plot-analysis"
                className="mt-5 inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 text-[13px] text-background-950 transition-colors hover:bg-accent-400"
              >
                进入专题
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}